import React, { useEffect, useState, useCallback } from 'react';
import { issuesAPI } from '../../services/api';
import { TableSkeleton, EmptyState } from '../../components/skeletons';

const S = {
  page: { padding: 24, background: 'var(--cream)', minHeight: '100vh' },
  headerArea: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 },
  title: { fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: 'var(--char)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)' },
  actionBar: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' },
  select: { padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, outline: 'none', background: '#fff', cursor: 'pointer' },
  btn: { background: '#4C1D95', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'opacity 0.2s', display: 'flex', alignItems: 'center', gap: 6 },
  btnSecondary: { background: '#fff', color: 'var(--char)', border: '1px solid var(--border)', padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'background-color 0.2s' },
  card: { background: '#fff', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.02)', marginBottom: 20 },
  table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  th: { padding: '14px 20px', background: '#f9fafb', fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--border)' },
  td: { padding: '16px 20px', fontSize: 14, color: 'var(--char)', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' },
  badge: { display: 'inline-block', padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' },
  iconBtn: { border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', padding: 6, borderRadius: 6, transition: 'background-color 0.2s', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
  modalCard: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 700, padding: 28, boxShadow: 'var(--shadow-xl)', position: 'relative', animation: 'scaleUp 0.2s ease-out' },
  modalTitle: { fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: 'var(--char)', marginBottom: 8 },
  historyRow: { padding: '14px 0', borderBottom: '1px solid var(--border)' },
  toast: { padding: '14px 18px', borderRadius: 10, marginBottom: 20, fontSize: 13, fontWeight: 500 }
};

export default function SystemIssues() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [role, setRole] = useState('');
  const [event, setEvent] = useState('');

  // Modal State
  const [showHistory, setShowHistory] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userHistory, setUserHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Toast State
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchIssues = useCallback(() => {
    setLoading(true);
    issuesAPI.list({ role, event })
      .then(res => {
        if (res.data.success) {
          setData(res.data.data);
        }
      })
      .catch(err => {
        console.error('Failed to fetch issues:', err);
        showToast('error', 'Failed to load system issues.');
      })
      .finally(() => setLoading(false));
  }, [role, event]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  // Fix an issue
  const handleFixIssue = (issueId, eventName) => {
    if (window.confirm(`Are you sure you want to mark the "${eventName}" issue as fixed?`)) {
      issuesAPI.fix(issueId)
        .then(res => {
          if (res.data.success) {
            showToast('success', 'Issue marked as fixed and user has been notified.');
            fetchIssues();
            if (showHistory && selectedUser) {
              // Refresh user history modal content
              loadUserHistory(selectedUser.userId);
            }
          }
        })
        .catch(err => {
          console.error(err);
          showToast('error', err.response?.data?.message || 'Failed to fix issue.');
        });
    }
  };

  // View user issues detailed history
  const loadUserHistory = (userId) => {
    setLoadingHistory(true);
    issuesAPI.userHistory(userId)
      .then(res => {
        if (res.data.success) {
          setUserHistory(res.data.data);
        }
      })
      .catch(err => {
        console.error(err);
        showToast('error', 'Failed to load user issue history.');
      })
      .finally(() => setLoadingHistory(false));
  };

  const handleViewUserHistory = (item) => {
    setSelectedUser({
      userId: item.userId,
      fullName: item.fullName,
      email: item.email,
      role: item.role
    });
    setShowHistory(true);
    loadUserHistory(item.userId);
  };

  const getStatusBadge = (status) => {
    if (status === 'fixed') {
      return <span style={{ ...S.badge, background: '#D1FAE5', color: '#065F46' }}>Fixed</span>;
    }
    return <span style={{ ...S.badge, background: '#FEE2E2', color: '#991B1B' }}>Pending</span>;
  };

  return (
    <div style={S.page}>
      <div style={S.headerArea}>
        <div>
          <div style={S.title}>System Issues</div>
          <div style={S.subtitle}>Monitor and resolve issues reported by buyers and vendors.</div>
        </div>
      </div>

      {toast && (
        <div style={{
          ...S.toast,
          background: toast.type === 'success' ? '#ECFDF5' : '#FEF2F2',
          color: toast.type === 'success' ? '#065F46' : '#991B1B',
          border: `1.5px solid ${toast.type === 'success' ? '#A7F3D0' : '#FCA5A5'}`
        }}>
          {toast.message}
        </div>
      )}

      {/* Filters Bar */}
      <div style={S.actionBar}>
        <select style={S.select} value={role} onChange={e => { setRole(e.target.value); setEvent(''); }}>
          <option value="">All Roles</option>
          <option value="buyer">Buyer</option>
          <option value="vendor">Vendor</option>
        </select>

        {/* Display sub-filter for event if role is selected or buyer is selected */}
        {role === 'buyer' && (
          <select style={S.select} value={event} onChange={e => setEvent(e.target.value)}>
            <option value="">All Events</option>
            <option value="post demand">Post Demand</option>
            <option value="accepting/rejecting vendor offer for your demand">Accepting/Rejecting Vendor Offer</option>
            <option value="bulk sale">Bulk Sale</option>
            <option value="chat functionality">Chat Functionality</option>
            <option value="app issue">App Issue</option>
          </select>
        )}

        {/* Also allow vendor sub-filter options if role is vendor */}
        {role === 'vendor' && (
          <select style={S.select} value={event} onChange={e => setEvent(e.target.value)}>
            <option value="">All Events</option>
            <option value="kyc process">KYC Process</option>
            <option value="wallet balance">Wallet Balance</option>
            <option value="sending offer to demands">Sending Offer to Demands</option>
            <option value="bulk sale">Bulk Sale</option>
            <option value="chat functionality">Chat Functionality</option>
            <option value="app issue">App Issue</option>
          </select>
        )}
      </div>

      {/* Main Table Card */}
      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Username</th>
              <th style={S.th}>Role</th>
              <th style={S.th}>Latest Event</th>
              <th style={S.th}>Latest Description</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <tr key={idx}>
                  <td colSpan="6" style={{ padding: '8px 16px' }}><TableSkeleton rows={1} columns={1} showHeader={false} /></td>
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: 24, textAlign: 'center' }}>
                  <EmptyState title="No system issues reported" description="There are no system issues matching your filter selection." />
                </td>
              </tr>
            ) : (
              data.map(item => (
                <tr key={item.userId}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 700 }}>{item.fullName}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>{item.email}</div>
                  </td>
                  <td style={S.td}>
                    <span style={{ textTransform: 'capitalize' }}>{item.role}</span>
                  </td>
                  <td style={S.td}>
                    <span style={{ fontWeight: 600 }}>{item.latestIssue.event}</span>
                  </td>
                  <td style={S.td}>
                    <div style={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.latestIssue.description}
                    </div>
                  </td>
                  <td style={S.td}>
                    {getStatusBadge(item.latestIssue.status)}
                  </td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <button
                        style={S.iconBtn}
                        title="View all user issues"
                        onClick={() => handleViewUserHistory(item)}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        👁️ View Details
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* User Issues History Detail Modal */}
      {showHistory && (
        <div style={S.modalOverlay} onClick={() => setShowHistory(false)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowHistory(false)}
              style={{ position: 'absolute', top: 20, right: 20, background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--muted)' }}
            >
              ✕
            </button>
            <div style={S.modalTitle}>User Issue Reports</div>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>
              Chronological log of reported issues for <strong>{selectedUser?.fullName}</strong> ({selectedUser?.email})
            </p>

            <div style={{ maxHeight: 400, overflowY: 'auto', paddingRight: 6 }}>
              {loadingHistory ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--muted)' }}>
                  Loading history...
                </div>
              ) : userHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--muted)' }}>
                  No issues recorded.
                </div>
              ) : (
                userHistory.map(issue => (
                  <div key={issue._id} style={S.historyRow}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        Event: {issue.event}
                      </div>
                      <div>
                        {getStatusBadge(issue.status)}
                      </div>
                    </div>
                    <div style={{ fontSize: 13, background: '#f9fafb', padding: '10px 14px', borderRadius: 8, marginTop: 6, fontStyle: 'italic', borderLeft: '3px solid #4C1D95' }}>
                      "{issue.description}"
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
                      <span>Reported Date: {new Date(issue.createdAt).toLocaleString()}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
              <button style={S.btnSecondary} onClick={() => setShowHistory(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
